/**
 * STOK SİPARİŞLERİ — eski `stockorder.php` + `stockorder_insert.php` karşılığı.
 *
 * Siparişten bağımsız "stoğa üretim" iş emirleri. Mod A statü kartları, Mod B kayıt listesi.
 *
 * KRİTİK KURAL
 *  `stockorder-completed` geçişi MAMUL STOĞUNU ARTIRIR → istemci Firestore'a yazamaz
 *  (Security Rules açıkça engeller). Bu geçiş yalnızca `completeStockOrder` callable'ı ile
 *  yapılır: transaction + `stockOrderReceipts` idempotency kilidi → aynı iş emri iki kez
 *  tamamlansa bile stok BİR KEZ artar. Eski `post.php` SELECT-sonra-UPDATE deseni yarış
 *  koşuluna açıktı ve mükerrer artışa izin veriyordu.
 *
 * Diğer statü geçişleri (yan etkisiz) doğrudan yazılır; statü sözlükten doğrulanır.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { doc, limit as fsLimit, orderBy, serverTimestamp, setDoc, updateDoc, where, type QueryConstraint } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";

import { StatusBadge } from "../components/StatusBadge";
import { Modal } from "../components/Modal";
import { ProductPicker, type PickedProduct } from "../components/ProductPicker";
import { Toast } from "../components/Toast";
import { Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { useCollection } from "../hooks/useCollection";
import { useStatusCounts } from "../hooks/useStatusCounts";
import { ListLimitNotice } from "../components/ListLimitNotice";
import type {
  CompleteStockOrderInput,
  CompleteStockOrderResult,
} from "../hooks/callableResults";
import { useSession } from "../lib/auth";
import { callable, db, storage } from "../lib/firebase";
import { date, n, since } from "../lib/format";
import { errorText } from "../lib/errors";
import { PageHeader } from "../components";
import { productionDocForStockOrder } from "../lib/productionFields";
import type { StatusDoc } from "../lib/types";

/** Statü teknik adı sözlükten gelir; bu sabit yalnızca "hangi geçiş callable ister" ayrımıdır. */
const COMPLETED_STATUS = "stockorder-completed";

interface StockOrderRow {
  id: string;
  productId?: string;
  productName?: string;
  productTitle?: string;
  variantTitle?: string;
  photoUrl?: string;
  quantity?: number;
  /** Göç betiği eski `volume` alanını taşır. */
  volume?: number;
  note?: string;
  status: string;
  priority?: string;
  createdAt?: unknown;
  productionId?: string;
  stockIncreaseDone?: boolean;
}

function safeFileName(name: string): string {
  const ext = (name.split(".").pop() ?? "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
  return `foto-${Date.now().toString(36)}.${ext || "jpg"}`;
}

/** Tek sayfada okunacak en fazla kayıt. Aşılırsa ekran bunu SÖYLER. */
const STOCKORDER_LIST_LIMIT = 300;

export default function StockOrders() {
  const [params] = useSearchParams();
  const statusParam = params.get("status") ?? "";
  const session = useSession();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetStatus, setTargetStatus] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ kind: "success" | "error"; message: string } | null>(null);

  const [product, setProduct] = useState<PickedProduct | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [note, setNote] = useState("");
  const [priority, setPriority] = useState<"normal" | "urgent">("normal");
  const [photo, setPhoto] = useState<File | null>(null);

  const soStatusConstraints = useMemo<QueryConstraint[]>(
    () => [
      where("category", "==", "stockorder"),
      where("obsolete", "==", false),
      orderBy("sortOrder", "asc"),
    ],
    [],
  );
  const { data: soStatuses, loading: statusesLoading } = useCollection<StatusDoc>(
    "statuses",
    soStatusConstraints,
  );

  const prodStatusConstraints = useMemo<QueryConstraint[]>(
    () => [
      where("category", "==", "production"),
      where("obsolete", "==", false),
      orderBy("sortOrder", "asc"),
    ],
    [],
  );
  const { data: productionStatuses } = useCollection<StatusDoc>("statuses", prodStatusConstraints);

  /**
   * ★ STATÜ KARTLARINDAKİ ADET ARTIK CANLI SAYILIR.
   *
   * Eskiden `counters/stockOrders.byStatus` haritasından okunuyordu ve O
   * DOKÜMANI YAZAN HİÇBİR KOD YOKTU — göçten kalma, donmuş bir rakamdı. Kart
   * "Üretilecek — 172 kayıt" derken listede 7 kayıt vardı; hiçbir yerde hata
   * çıkmıyor, ekran sessizce yanlış sayı gösteriyordu.
   */
  const soStatusIds = useMemo(() => soStatuses.map((s) => s.id), [soStatuses]);
  const { counts: statusCounts, loading: countsLoading } = useStatusCounts(
    "stockOrders",
    "status",
    soStatusIds,
  );

  const listConstraints = useMemo<QueryConstraint[]>(
    () =>
      statusParam
        ? [where("status", "==", statusParam), orderBy("createdAt", "desc"), fsLimit(STOCKORDER_LIST_LIMIT)]
        : [where("status", "==", "__none__")],
    [statusParam],
  );
  const { data: rows, loading, error } = useCollection<StockOrderRow>("stockOrders", listConstraints);

  /**
   * ★ `useCallableAction` DEĞİL, doğrudan `callable()`.
   *
   * O kanca hatayı bilerek FIRLATMAZ: `null` döner ve mesajı React durumuna
   * yazar (`hooks/useCallableAction.ts:89`). Tek çağrıda sorun değil ama burada
   * iş emirleri DÖNGÜYLE tek tek tamamlanıyor; döngü içinde durum güncellemesi
   * okunamayacağı için hangi iş emrinin NİÇİN tamamlanmadığı kayboluyordu.
   * Doğrudan çağrıda `catch` gerçekten ulaşılabilir ve sebep kayda bağlanır —
   * `createStockOrder` bu dosyada zaten aynı deseni kullanıyor.
   */
  const completeStockOrder = useMemo(
    () => callable<CompleteStockOrderInput, CompleteStockOrderResult>("completeStockOrder"),
    [],
  );

  useEffect(() => {
    if (!targetStatus && soStatuses.length > 0) setTargetStatus(soStatuses[0]!.id);
  }, [soStatuses, targetStatus]);

  const initialProduction =
    productionStatuses.find((s) => s.id === "prod-hold") ?? productionStatuses[0];
  const initialProductionStatus = initialProduction?.id ?? "";
  /** Etiket de yazılır; üretim listesi statü sözlüğünü ikinci kez okumasın. */
  const initialProductionLabel = initialProduction?.label ?? "";
  const currentLabel = soStatuses.find((s) => s.id === statusParam)?.label ?? statusParam;

  function toggle(set: Set<string>, id: string): Set<string> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  /**
   * ★ SUNUCU CALLABLE'I ÇAĞRILIR — istemci artık doğrudan Firestore'a YAZMAZ.
   *
   * Eski hâlinde form `stockOrders` koleksiyonuna elle doküman ekliyordu ve
   * sunucunun beklediğinden BAŞKA alan adları yazıyordu (`quantity` yerine
   * `volume`, `productName` yerine `productTitle`). Sonuçta:
   *   · `recipeOwnerId` hiç yazılmıyordu → kesim listesi reçeteyi bulamıyordu
   *   · `inventoryKey` hiç yazılmıyordu → tamamlanınca stok artmıyordu
   *   · üretim kaydı HİÇ açılmıyordu → iş emri üretim listesinde görünmüyordu
   * Callable bunların hepsini tek yerde ve doğru adlarla yapar; üretim kaydını
   * da aynı anda açar, yani ürün panelden doğrudan üretime düşer.
   */
  async function createStockOrder() {
    const uid = session?.user.uid;
    if (!uid) return;
    const qty = Number(quantity.replace(",", "."));

    if (!product) {
      setToast({ kind: "error", message: "Katalogdan bir ürün seçin." });
      return;
    }
    if (!Number.isFinite(qty) || qty < 1) {
      setToast({ kind: "error", message: "Miktar en az 1 olmalıdır." });
      return;
    }

    setBusy(true);
    try {
      // Fotoğraf ÖNCE yüklenir: callable yolu bekliyor ve kayıt açıldıktan sonra
      // yükleme başarısız olursa fotoğrafsız bir kayıt kalırdı.
      let photoPath = "";
      let photoUrl = "";
      if (photo) {
        const path = `stockOrders/tmp/${uid}/${Date.now()}-${safeFileName(photo.name)}`;
        const r = storageRef(storage, path);
        await uploadBytes(r, photo, { customMetadata: { uploadedBy: uid } });
        photoUrl = await getDownloadURL(r);
        photoPath = path;
      }

      const out = await callable<
        { productId: string; volume: number; note?: string; priority: "normal" | "urgent"; photoPath?: string; photoUrl?: string },
        { stockOrderId: string; productionId: string }
      >("createStockOrder")({
        productId: product.id,
        volume: Math.round(qty),
        note: note.trim(),
        priority,
        ...(photoPath ? { photoPath, photoUrl } : {}),
      });

      setToast({
        kind: "success",
        message: `${product.name} × ${Math.round(qty)} üretime verildi (${out.stockOrderId}).`,
      });
      setFormOpen(false);
      setProduct(null);
      setQuantity("1");
      setNote("");
      setPriority("normal");
      setPhoto(null);
    } catch (e) {
      setToast({ kind: "error", message: errorText(e, "Stok siparişi açılamadı.") });
    } finally {
      setBusy(false);
    }
  }

  /**
   * TAMAMLAMA — mamul stok artışı yalnızca `completeStockOrder` callable'ı ile.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * KALDIRILAN ÖLÜ KOD: "yetersiz stok" penceresi ve "Yine de tamamla" akışı
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Burada bir `try/catch` vardı; `catch` bloğu `extractShortages(e)` ile eksik
   * bileşen listesini çıkarıp `ShortageModal`'ı açıyor, kullanıcı "Yine de
   * tamamla" deyince `allowNegative: true` ile yeniden deniyordu. Bu akışın
   * TAMAMI çalışmıyordu ve üç ayrı sebepten çalışamazdı:
   *
   *  1. `useCallableAction.run` ASLA FIRLATMAZ (`useCallableAction.ts:82-98`);
   *     hatayı yakalayıp `null` döner. Yani `catch` bloğuna hiç girilmiyordu.
   *
   *  2. Kanca düzeltilse bile gösterecek bir şey yoktu: `completeStockOrder`
   *     eksik stok DÖNDÜRMEZ ve `INSUFFICIENT_STOCK` FIRLATMAZ. Çağırdığı
   *     `receiveStockOrder` (`functions/src/stock/receive.ts`) yalnızca MAMUL
   *     stoğunu ARTIRIR; hammadde düşümü — ve dolayısıyla eksik listesi —
   *     bağlı üretim kaydının tamamlanmasında, `completeProduction` yolunda
   *     yapılır. `Production.tsx`'in `useDetailedCallable` deseni ORADA
   *     doğrudur çünkü orada gerçekten `details.shortages` gelir.
   *
   *  3. "Yine de tamamla" düğmesi bir şey yapmazdı: `allowNegative` girdi
   *     şemasında kabul ediliyor (`core/validate.ts:150`) ama
   *     `completeStockOrder` onu `receiveStockOrder`'a HİÇ İLETMİYOR
   *     (`stock/index.ts:199-203`).
   *
   * Bu yüzden desen kopyalanmadı, ölü kod KALDIRILDI: var olmayan bir hatayı
   * yakalayan pencere ve hiçbir şey yapmayan bir "zorla tamamla" düğmesi
   * tutmak, kullanıcıya olmayan bir yeteneği vaat etmek olurdu. Bu ekranın
   * gerçek hata sınıfı üretimin tamamlanmamış olmasıdır ve sunucu bunu
   * `INVALID_STATUS_TRANSITION` ile Türkçe açıklıyor (`receive.ts:136-142`);
   * o mesaj artık aşağıda AYNEN kullanıcıya gösteriliyor.
   *
   * ★ KISMİ BAŞARI ARTIK GÖRÜNÜR: eski kod tek bir hata olduğunda yalnızca
   *   hata satırını yazıyor, gerçekten tamamlanan iş emirlerini SÖYLEMİYORDU.
   */
  async function completeMany(ids: string[]) {
    setBusy(true);
    const failures: string[] = [];
    let done = 0;
    let already = 0;

    for (const id of ids) {
      try {
        const res = await completeStockOrder({ stockOrderId: id });
        if (res.alreadyProcessed) already += 1;
        else done += 1;
      } catch (e) {
        // Sunucu mesajı Türkçe üretilir; yeniden yazılmaz, yumuşatılmaz.
        failures.push(`${id}: ${errorText(e, "Tamamlanamadı.")}`);
      }
    }

    setBusy(false);
    setSelected(new Set());

    const parts: string[] = [];
    if (done > 0) parts.push(`${done} iş emri tamamlandı, mamul stoğu arttı.`);
    if (already > 0) {
      parts.push(`${already} iş emri zaten tamamlanmıştı, stok tekrar artırılmadı.`);
    }
    if (failures.length > 0) {
      parts.push(`${failures.length} iş emri tamamlanamadı → ${failures.join(" · ")}`);
    }
    if (parts.length === 0) parts.push("İşlenecek iş emri seçilmedi.");

    setToast({
      kind: failures.length > 0 ? "error" : "success",
      message: parts.join(" "),
    });
  }

  async function changeStatus() {
    const uid = session?.user.uid;
    if (!uid) return;
    if (selected.size === 0 || !targetStatus) {
      setToast({ kind: "error", message: "Kayıt ve hedef statü seçilmelidir." });
      return;
    }

    // Stok artıran geçiş istemciden YAZILMAZ.
    if (targetStatus === COMPLETED_STATUS) {
      await completeMany([...selected]);
      return;
    }

    setBusy(true);
    const failures: string[] = [];
    for (const id of selected) {
      try {
        await updateDoc(doc(db, "stockOrders", id), {
          status: targetStatus,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        });
      } catch (e) {
        failures.push(`${id}: ${errorText(e)}`);
      }
    }
    setBusy(false);
    setSelected(new Set());
    setToast(
      failures.length > 0
        ? { kind: "error", message: `Güncellenemeyen kayıtlar → ${failures.join(" · ")}` }
        : { kind: "success", message: "Statü güncellendi." },
    );
  }

  async function sendToProduction() {
    const uid = session?.user.uid;
    if (!uid) return;
    if (selected.size === 0) {
      setToast({ kind: "error", message: "Önce kayıt seçin." });
      return;
    }
    if (!initialProductionStatus) {
      setToast({ kind: "error", message: "Üretim statü sözlüğü boş." });
      return;
    }
    setBusy(true);
    const failures: string[] = [];
    let sent = 0;
    for (const id of selected) {
      const row = rows.find((r) => r.id === id);
      try {
        /**
         * ★ DOKÜMAN KİMLİĞİ ÖNCE KAYDIN KENDİSİNDEN OKUNUR.
         *
         * Göç edilmiş 179 iş emrinin üretim kaydı `production/stock_{id}`
         * adıyla duruyor; `core/ids.ts` ise yeni kayıtlar için
         * `stockOrder_{id}` üretiyor. Sabit `stockOrder_${id}` yazılırsa aynı
         * iş emri için İKİNCİ bir üretim kaydı doğar — mimarinin yapısal
         * olarak kapattığı mükerrer kayıt, ad ayrışmasından geri gelir.
         */
        const prodDocId = row?.productionId?.trim() || `stockOrder_${id}`;
        // Gövde okuyan ekranla aynı yardımcıdan gelir: ürün adı, adet ve not
        // üretim listesinde görünür (eskiden yalnızca kimlik yazılıyordu).
        await setDoc(
          doc(db, "production", prodDocId),
          productionDocForStockOrder(
            { id, ...(row ?? {}) },
            {
              status: initialProductionStatus,
              statusLabel: initialProductionLabel,
              uid,
              priority: row?.priority,
            },
          ),
        );
        sent += 1;
      } catch (e) {
        failures.push(`${id}: ${errorText(e, "Zaten üretimde olabilir.")}`);
      }
    }
    setBusy(false);
    setSelected(new Set());
    setToast(
      failures.length > 0
        ? { kind: "error", message: `${sent} kayıt gönderildi. Hatalar → ${failures.join(" · ")}` }
        : { kind: "success", message: `${sent} stok siparişi üretime gönderildi.` },
    );
  }

  const form = (
    <Modal open={formOpen} title="Yeni Stok Siparişi" onClose={() => setFormOpen(false)}>
      <div className="space-y-4">
        {/* Ürün KATALOGDAN seçilir. Eskiden Shopify varyant kimliği elle
            yazılıyordu; yanlış/boş kimlik girilen kayıt hiçbir ürüne bağlanmıyor,
            reçete bulunamıyor ve kesim listesi boş çıkıyordu. */}
        <ProductPicker value={product} onChange={setProduct} />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col">
            <label htmlFor="so-qty" className="text-base font-semibold text-gray-800">
              Miktar
            </label>
            <input
              id="so-qty"
              inputMode="numeric"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="mt-1 min-h-[48px] rounded-md border border-gray-300 px-3 text-base"
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="so-priority" className="text-base font-semibold text-gray-800">
              Öncelik
            </label>
            <select
              id="so-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as "normal" | "urgent")}
              className="mt-1 min-h-[48px] rounded-md border border-gray-300 px-3 text-base"
            >
              <option value="normal">Normal</option>
              <option value="urgent">Acil</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col">
          <label htmlFor="so-note" className="text-base font-semibold text-gray-800">
            Açıklama
          </label>
          <textarea
            id="so-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 p-3 text-base"
          />
        </div>

        <div className="flex flex-col">
          <label htmlFor="so-photo" className="text-base font-semibold text-gray-800">
            Fotoğraf (jpg, png, webp)
          </label>
          <input
            id="so-photo"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            className="mt-1 min-h-[48px] rounded-md border border-gray-300 p-2 text-base"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setFormOpen(false)}
            className="inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
          >
            Vazgeç
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void createStockOrder()}
            className="inline-flex min-h-[48px] items-center rounded-lg bg-gray-900 px-5 py-3 text-base font-bold text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {busy ? "Kaydediliyor…" : "Ekle"}
          </button>
        </div>
      </div>
    </Modal>
  );

  /* ── Mod A: statü kartları ───────────────────────────────────────────────── */
  if (!statusParam) {
    return (
      <PageHeader title="Stok Siparişleri">
        {toast ? <Toast kind={toast.kind} message={toast.message} onClose={() => setToast(null)} /> : null}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="inline-flex min-h-[48px] items-center rounded-md bg-red-500 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-700"
          >
            Yeni Stok Siparişi Ekle
          </button>
        </div>

        {statusesLoading ? (
          <Spinner />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {soStatuses.map((s) => (
              <Link
                key={s.id}
                to={`?status=${encodeURIComponent(s.id)}`}
                className="flex min-h-[76px] items-center justify-between gap-3 rounded-lg bg-white p-4 text-base shadow-sm ring-1 ring-gray-200 hover:ring-primary-300"
              >
                <span className="font-semibold text-gray-900">{s.label}</span>
                {/* Sayılamayan statüde "—" yazar; SIFIR yazmaz. "Sayılamadı"
                    ile "hiç kayıt yok" aynı şey değil ve burada sıfır görmek
                    "iş bitti" demek olurdu. */}
                <span className="text-gray-500">
                  {countsLoading
                    ? "sayılıyor…"
                    : statusCounts[s.id] === undefined
                      ? "—"
                      : `${statusCounts[s.id]} kayıt`}
                </span>
              </Link>
            ))}
          </div>
        )}
        {form}
      </PageHeader>
    );
  }

  /* ── Mod B: kayıt listesi ────────────────────────────────────────────────── */
  return (
    <PageHeader title={`Stok Siparişleri — ${currentLabel}`}>
      {toast ? <Toast kind={toast.kind} message={toast.message} onClose={() => setToast(null)} /> : null}

      {/* `ShortageModal` buradaydı — kaldırıldı; gerekçesi `completeMany`
          başlığında: `completeStockOrder` eksik stok döndürmüyor, o pencere
          hiçbir koşulda açılamıyordu. */}

      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        <Link
          to="/stock-orders"
          className="inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          ← Statüler
        </Link>
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="inline-flex min-h-[48px] items-center rounded-md bg-red-500 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-700"
        >
          Yeni Stok Siparişi Ekle
        </button>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-base font-medium text-gray-600">
            {selected.size > 0 ? `${selected.size} kayıt seçili` : "Kayıt seçin"}
          </span>
          <select
            value={targetStatus}
            onChange={(e) => setTargetStatus(e.target.value)}
            className="min-h-[48px] rounded-md border-0 bg-white px-4 py-3 text-base font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300"
          >
            {soStatuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={busy || selected.size === 0}
            onClick={() => void changeStatus()}
            className="inline-flex min-h-[48px] items-center rounded-md bg-red-500 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {targetStatus === COMPLETED_STATUS ? "Tamamla (stok +)" : "Statü Değiş"}
          </button>
          <button
            type="button"
            disabled={busy || selected.size === 0}
            onClick={() => void sendToProduction()}
            className="inline-flex min-h-[48px] items-center rounded-lg bg-gray-900 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Üretime Gönder
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-base text-red-700">
          Liste yüklenemedi: {error}
        </div>
      ) : null}

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState title={`${currentLabel} statüsünde iş emri yok`} />
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const isSelected = selected.has(r.id);
            const isOpen = expanded.has(r.id);
            const qty = r.quantity ?? r.volume ?? 0;
            return (
              <div key={r.id} className="rounded-lg bg-white shadow-lg">
                <div
                  className={`flex min-h-[88px] items-center justify-between gap-x-4 rounded-lg px-4 py-4 sm:px-6 ${
                    isSelected ? "ring-2 ring-inset ring-blue-700" : ""
                  }`}
                >
                  <label className="flex flex-1 cursor-pointer items-center gap-x-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => setSelected((s) => toggle(s, r.id))}
                      className="h-6 w-6 flex-none rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="min-w-0 flex-auto">
                      <span className="block text-base font-semibold text-gray-900">
                        Stok Siparişi-{r.id}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                          {r.productName ?? r.productTitle ?? "Ürün Bulunamadı"}
                        </span>
                        <StatusBadge technicName={r.status} />
                        {r.priority === "urgent" || r.priority === "Acil" ? (
                          <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
                            Acil
                          </span>
                        ) : null}
                        {r.stockIncreaseDone ? (
                          <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
                            Stoğa girdi
                          </span>
                        ) : null}
                        <span>{since(r.createdAt as never)}</span>
                      </span>
                    </span>
                  </label>

                  <div className="hidden flex-col items-end sm:flex">
                    <span className="text-base text-gray-900">{n(qty, " adet")}</span>
                    <span className="mt-1 text-xs text-gray-500">{date(r.createdAt as never)}</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setExpanded((s) => toggle(s, r.id))}
                    aria-label="Detayı göster"
                    className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                  >
                    {isOpen ? "▲" : "▼"}
                  </button>
                </div>

                {isOpen ? (
                  <div className="flex flex-wrap gap-4 rounded-b-lg bg-gray-100 p-4 shadow-inner">
                    <div className="w-full sm:w-1/4">
                      {r.photoUrl ? (
                        <img
                          src={r.photoUrl}
                          alt=""
                          loading="lazy"
                          className="h-40 w-full rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-40 w-full items-center justify-center rounded bg-gray-200 text-sm text-gray-500">
                          Fotoğraf yok
                        </div>
                      )}
                    </div>
                    <div className="flex-1 space-y-2 text-base text-gray-800">
                      <p>Açıklama: {r.note || "—"}</p>
                      <p className="text-sm text-gray-600">
                        Ürün: {r.productName ?? r.productId ?? "—"}
                        {r.variantTitle ? ` — ${r.variantTitle}` : ""} · Miktar: {n(qty)}
                      </p>
                      {r.productionId ? (
                        <Link
                          to={`/production/${encodeURIComponent(r.productionId)}`}
                          className="text-base font-medium text-blue-600 hover:text-blue-800"
                        >
                          Üretim kaydına git
                        </Link>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* ★ SESSİZ KESME YOK — kart ile liste ayrışırsa ekran SÖYLER. */}
      {statusParam ? (
        <ListLimitNotice
          shown={rows.length}
          total={statusCounts[statusParam]}
          limit={STOCKORDER_LIST_LIMIT}
        />
      ) : null}

      {form}
    </PageHeader>
  );
}
